import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, FileText, FileUp, History as HistoryIcon,
  Search, BookOpen, Download as DownloadIcon,
} from "lucide-react";

const SECTIONS = [
  "security", "compliance", "operations", "documentation",
  "downloads", "release-notes", "api-integrations", "support",
];

export default function PartnerContentPanel() {
  const [subtab, setSubtab] = useState("pages");
  return (
    <div data-testid="atc-panel-partner-content">
      <div className="hw-dev__tabs" style={{ borderBottomColor: "#E5E7EB",
                                                 marginTop: 8 }}>
        {[
          ["pages", "Pages", BookOpen],
          ["documents", "Documents", FileUp],
          ["releases", "Release Notes", HistoryIcon],
        ].map(([id, label, Icon]) => (
          <button key={id}
            className={`hw-dev__tab ${subtab === id ? "hw-dev__tab--active" : ""}`}
            style={{ color: subtab === id ? "#0F172A" : "#64748B" }}
            onClick={() => setSubtab(id)}
            data-testid={`atc-pc-tab-${id}`}>
            <Icon className="w-3.5 h-3.5" style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.25rem" }} />
            {label}
          </button>
        ))}
      </div>
      {subtab === "pages" && <PagesAdmin />}
      {subtab === "documents" && <DocumentsAdmin />}
      {subtab === "releases" && <ReleasesAdmin />}
    </div>
  );
}

// ── Pages CRUD ──────────────────────────────────────────────────────────────
function PagesAdmin() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [section, setSection] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/trust-center/partner-content/pages",
        { params: { search: q || undefined, section: section || undefined,
                     include_drafts: true, page_size: 100 } });
      setList(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [q, section]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this page? (Soft delete — recoverable via DB.)")) return;
    try {
      await api.delete(`/admin/trust-center/partner-content/pages/${id}`);
      toast.success("Page deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="hw-partners__section" data-testid="atc-pc-pages">
      <header style={{ display: "flex", justifyContent: "space-between",
                         alignItems: "center", flexWrap: "wrap", gap: 8,
                         marginBottom: 10 }}>
        <div style={{ display: "flex", gap: 8, flex: 1 }}>
          <input placeholder="Search pages…" value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="hw-partners__input"
                  data-testid="atc-pc-page-search" />
          <select value={section} onChange={(e) => setSection(e.target.value)}
                    className="hw-partners__input" style={{ maxWidth: 200 }}
                    data-testid="atc-pc-page-section">
            <option value="">All sections</option>
            {SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          data-testid="atc-pc-page-new-btn"
          className="hw-partners__btn hw-partners__btn--primary">
          <Plus className="w-3.5 h-3.5" /> New page
        </button>
      </header>

      {showForm && (
        <PageForm initial={editing}
                     onClose={() => { setShowForm(false); setEditing(null); }}
                     onSaved={() => { setShowForm(false); setEditing(null); load(); }} />
      )}

      {!list ? <Loader2 className="w-4 h-4 animate-spin" /> :
        (<table className="hw-partners__table" data-testid="atc-pc-pages-table">
          <thead><tr><th>Title</th><th>Section</th><th>Slug</th><th>Status</th><th>v</th><th></th></tr></thead>
          <tbody>
            {list.items.map((p) => (
              <tr key={p.id} data-testid={`atc-pc-page-row-${p.slug}`}>
                <td>{p.title}</td>
                <td>{p.section}</td>
                <td><code>{p.slug}</code></td>
                <td><span className={`hw-pill hw-pill--${p.status === "published" ? "ok" : "muted"}`}>{p.status}</span></td>
                <td>v{p.version}</td>
                <td style={{ textAlign: "right" }}>
                  <button onClick={() => { setEditing(p); setShowForm(true); }}
                    data-testid={`atc-pc-page-edit-${p.slug}`}
                    className="hw-partners__btn hw-partners__btn--sm">Edit</button>
                  <button onClick={() => del(p.id)}
                    data-testid={`atc-pc-page-delete-${p.slug}`}
                    className="hw-partners__btn hw-partners__btn--danger hw-partners__btn--sm">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>)
      }
    </div>
  );
}

function PageForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState({
    section: initial?.section || "security",
    slug: initial?.slug || "",
    title: initial?.title || "",
    summary: initial?.summary || "",
    body_md: initial?.body_md || "",
    status: initial?.status || "published",
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      if (initial) {
        await api.patch(`/admin/trust-center/partner-content/pages/${initial.id}`, f);
      } else {
        await api.post("/admin/trust-center/partner-content/pages", f);
      }
      toast.success(initial ? "Page updated" : "Page created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="hw-partners__panel"
          style={{ background: "#F8FAFC", padding: 12, marginBottom: 12 }}
          data-testid="atc-pc-page-form">
      <div style={{ display: "grid",
                     gridTemplateColumns: "160px 1fr 200px 120px",
                     gap: 8, marginBottom: 8 }}>
        <select value={f.section} disabled={!!initial}
                 onChange={(e) => setF({ ...f, section: e.target.value })}
                 className="hw-partners__input"
                 data-testid="atc-pc-form-section">
          {SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={f.title} placeholder="Title"
                onChange={(e) => setF({ ...f, title: e.target.value })}
                className="hw-partners__input"
                data-testid="atc-pc-form-title" />
        <input value={f.slug} placeholder="slug (kebab-case)"
                disabled={!!initial}
                onChange={(e) => setF({ ...f, slug: e.target.value })}
                className="hw-partners__input"
                data-testid="atc-pc-form-slug" />
        <select value={f.status}
                 onChange={(e) => setF({ ...f, status: e.target.value })}
                 className="hw-partners__input"
                 data-testid="atc-pc-form-status">
          <option value="draft">draft</option>
          <option value="published">published</option>
          <option value="archived">archived</option>
        </select>
      </div>
      <input value={f.summary} placeholder="Summary (1 line)"
              onChange={(e) => setF({ ...f, summary: e.target.value })}
              className="hw-partners__input" style={{ width: "100%",
                                                          marginBottom: 8 }}
              data-testid="atc-pc-form-summary" />
      {preview ? (
        <div style={{ padding: 12, background: "white", borderRadius: 6,
                        border: "1px solid #E5E7EB", minHeight: 200 }}
              data-testid="atc-pc-form-preview">
          <ReactMarkdown>{f.body_md}</ReactMarkdown>
        </div>
      ) : (
        <textarea value={f.body_md} rows={10}
                    placeholder="Body (markdown)"
                    onChange={(e) => setF({ ...f, body_md: e.target.value })}
                    className="hw-partners__input"
                    style={{ width: "100%", fontFamily: "monospace" }}
                    data-testid="atc-pc-form-body" />
      )}
      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
        <button onClick={() => setPreview(!preview)}
          className="hw-partners__btn hw-partners__btn--sm"
          data-testid="atc-pc-form-preview-toggle">
          {preview ? "Edit" : "Preview"}
        </button>
        <span style={{ flex: 1 }} />
        <button onClick={onClose}
          className="hw-partners__btn hw-partners__btn--sm"
          data-testid="atc-pc-form-cancel">Cancel</button>
        <button onClick={save} disabled={busy}
          data-testid="atc-pc-form-save"
          className="hw-partners__btn hw-partners__btn--primary hw-partners__btn--sm">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          {initial ? "Save changes" : "Create page"}
        </button>
      </div>
    </div>
  );
}

// ── Documents ───────────────────────────────────────────────────────────────
function DocumentsAdmin() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [section, setSection] = useState("");
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);
  const [showUpload, setShowUpload] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/trust-center/partner-content/documents",
        { params: { search: q || undefined, section: section || undefined,
                     page_size: 50 } });
      setList(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [q, section]);
  useEffect(() => { load(); }, [load]);

  const upload = async (form) => {
    setBusy(true);
    try {
      await api.post("/admin/trust-center/partner-content/documents", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setShowUpload(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/admin/trust-center/partner-content/documents/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="hw-partners__section" data-testid="atc-pc-documents">
      <header style={{ display: "flex", justifyContent: "space-between",
                         alignItems: "center", flexWrap: "wrap", gap: 8,
                         marginBottom: 10 }}>
        <div style={{ display: "flex", gap: 8, flex: 1 }}>
          <input placeholder="Search documents…" value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="hw-partners__input"
                  data-testid="atc-pc-doc-search" />
          <select value={section} onChange={(e) => setSection(e.target.value)}
                    className="hw-partners__input" style={{ maxWidth: 200 }}
                    data-testid="atc-pc-doc-section">
            <option value="">All sections</option>
            {SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={() => setShowUpload(true)}
          data-testid="atc-pc-doc-upload-btn"
          className="hw-partners__btn hw-partners__btn--primary">
          <FileUp className="w-3.5 h-3.5" /> Upload
        </button>
      </header>

      {showUpload && (
        <UploadForm sections={SECTIONS} busy={busy}
                       onCancel={() => setShowUpload(false)}
                       onSubmit={upload} />
      )}

      {!list ? <Loader2 className="w-4 h-4 animate-spin" /> :
        (<table className="hw-partners__table" data-testid="atc-pc-doc-table">
          <thead>
            <tr><th>Title</th><th>Section</th><th>v</th>
                <th>Size</th><th>Downloads</th><th>Uploaded</th><th></th></tr>
          </thead>
          <tbody>
            {list.items.map((d) => (
              <tr key={d.id} data-testid={`atc-pc-doc-row-${d.id.slice(0, 8)}`}>
                <td><strong>{d.title}</strong>
                    <div style={{ fontSize: 11, color: "#64748B" }}>{d.filename}</div></td>
                <td>{d.section}</td>
                <td>v{d.version}</td>
                <td>{d.size >= 1024 * 1024 ? `${(d.size / (1024*1024)).toFixed(1)} MB`
                                              : `${(d.size / 1024).toFixed(1)} KB`}</td>
                <td>{d.download_count || 0}</td>
                <td>{new Date(d.created_at).toLocaleDateString()}</td>
                <td>
                  <button onClick={() => del(d.id)}
                    data-testid={`atc-pc-doc-delete-${d.id.slice(0, 8)}`}
                    className="hw-partners__btn hw-partners__btn--danger hw-partners__btn--sm">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>)}
    </div>
  );
}

function UploadForm({ sections, busy, onCancel, onSubmit }) {
  const fileInput = useRef(null);
  const [meta, setMeta] = useState({ section: "documentation",
                                       title: "", summary: "" });
  const submit = () => {
    const f = fileInput.current?.files?.[0];
    if (!f) { toast.error("Pick a file first"); return; }
    const form = new FormData();
    form.append("file", f);
    form.append("section", meta.section);
    if (meta.title) form.append("title", meta.title);
    if (meta.summary) form.append("summary", meta.summary);
    onSubmit(form);
  };
  return (
    <div className="hw-partners__panel"
          style={{ background: "#F8FAFC", padding: 12, marginBottom: 12 }}
          data-testid="atc-pc-doc-upload-form">
      <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 1fr",
                     gap: 8, marginBottom: 8 }}>
        <select value={meta.section}
                 onChange={(e) => setMeta({ ...meta, section: e.target.value })}
                 className="hw-partners__input"
                 data-testid="atc-pc-doc-upload-section">
          {sections.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input placeholder="Display title (optional)"
                value={meta.title}
                onChange={(e) => setMeta({ ...meta, title: e.target.value })}
                className="hw-partners__input"
                data-testid="atc-pc-doc-upload-title" />
        <input placeholder="Summary (optional)"
                value={meta.summary}
                onChange={(e) => setMeta({ ...meta, summary: e.target.value })}
                className="hw-partners__input"
                data-testid="atc-pc-doc-upload-summary" />
      </div>
      <input ref={fileInput} type="file"
              data-testid="atc-pc-doc-upload-file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt,.md,.csv,.png,.jpg" />
      <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end",
                     gap: 8 }}>
        <button onClick={onCancel}
          data-testid="atc-pc-doc-upload-cancel"
          className="hw-partners__btn hw-partners__btn--sm">Cancel</button>
        <button onClick={submit} disabled={busy}
          data-testid="atc-pc-doc-upload-submit"
          className="hw-partners__btn hw-partners__btn--primary hw-partners__btn--sm">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          Upload
        </button>
      </div>
    </div>
  );
}

// ── Releases ────────────────────────────────────────────────────────────────
function ReleasesAdmin() {
  const [list, setList] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/trust-center/partner-content/releases",
        { params: { page_size: 50 } });
      setList(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this release?")) return;
    try {
      await api.delete(`/admin/trust-center/partner-content/releases/${id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="hw-partners__section" data-testid="atc-pc-releases">
      <header style={{ display: "flex", justifyContent: "space-between",
                         marginBottom: 10 }}>
        <div />
        <button onClick={() => setShowForm(true)}
          data-testid="atc-pc-release-new"
          className="hw-partners__btn hw-partners__btn--primary">
          <Plus className="w-3.5 h-3.5" /> New release
        </button>
      </header>
      {showForm && <ReleaseForm onClose={() => setShowForm(false)}
                                    onSaved={() => { setShowForm(false); load(); }} />}
      {!list ? <Loader2 className="w-4 h-4 animate-spin" /> :
        <table className="hw-partners__table" data-testid="atc-pc-release-table">
          <thead><tr><th>Version</th><th>Title</th><th>Released</th><th></th></tr></thead>
          <tbody>
            {list.items.map((r) => (
              <tr key={r.id} data-testid={`atc-pc-release-row-${r.version.replace(/\./g, '-')}`}>
                <td><code>{r.version}</code></td>
                <td>{r.title}</td>
                <td>{new Date(r.released_at).toLocaleDateString()}</td>
                <td>
                  <button onClick={() => del(r.id)}
                    data-testid={`atc-pc-release-delete-${r.version.replace(/\./g, '-')}`}
                    className="hw-partners__btn hw-partners__btn--danger hw-partners__btn--sm">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>}
    </div>
  );
}

function ReleaseForm({ onClose, onSaved }) {
  const [f, setF] = useState({
    version: "", released_at: new Date().toISOString().slice(0, 10),
    title: "", features: "", fixes: "", security_notes: "",
    breaking_changes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/admin/trust-center/partner-content/releases", {
        version: f.version.trim(),
        released_at: new Date(f.released_at).toISOString(),
        title: f.title.trim(),
        features: f.features.split("\n").filter(Boolean),
        fixes: f.fixes.split("\n").filter(Boolean),
        security_notes: f.security_notes.split("\n").filter(Boolean),
        breaking_changes: f.breaking_changes.split("\n").filter(Boolean),
        downloads: [],
      });
      toast.success("Release published");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="hw-partners__panel"
          style={{ background: "#F8FAFC", padding: 12, marginBottom: 12 }}
          data-testid="atc-pc-release-form">
      <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 160px",
                     gap: 8, marginBottom: 8 }}>
        <input placeholder="v1.2.3" value={f.version}
                onChange={(e) => setF({ ...f, version: e.target.value })}
                className="hw-partners__input"
                data-testid="atc-pc-release-version" />
        <input placeholder="Release title" value={f.title}
                onChange={(e) => setF({ ...f, title: e.target.value })}
                className="hw-partners__input"
                data-testid="atc-pc-release-title" />
        <input type="date" value={f.released_at}
                onChange={(e) => setF({ ...f, released_at: e.target.value })}
                className="hw-partners__input"
                data-testid="atc-pc-release-date" />
      </div>
      {[
        ["features", "✨ Features (one per line)"],
        ["fixes", "🐞 Bug fixes"],
        ["security_notes", "🔒 Security improvements"],
        ["breaking_changes", "⚠️ Breaking changes"],
      ].map(([k, label]) => (
        <div key={k} style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 11, color: "#64748B",
                          textTransform: "uppercase", marginBottom: 2 }}>
            {label}
          </div>
          <textarea rows={3} value={f[k]}
                      onChange={(e) => setF({ ...f, [k]: e.target.value })}
                      className="hw-partners__input" style={{ width: "100%" }}
                      data-testid={`atc-pc-release-${k}`} />
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onClose}
          data-testid="atc-pc-release-cancel"
          className="hw-partners__btn hw-partners__btn--sm">Cancel</button>
        <button onClick={submit} disabled={busy || !f.version || !f.title}
          data-testid="atc-pc-release-save"
          className="hw-partners__btn hw-partners__btn--primary hw-partners__btn--sm">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          Publish release
        </button>
      </div>
    </div>
  );
}
